<template>
  <div :class="{ 'mb-4': !embedded }">
    <div v-if="!embedded" class="text-subtitle-2 mb-1">{{ label }}</div>
    <v-card outlined class="pa-2">
      <!-- image -->
      <v-row v-if="isImage" dense align="center">
        <v-col v-if="previewUrl" cols="auto">
          <v-img :src="previewUrl" width="80" height="80" contain class="grey lighten-4 rounded" />
        </v-col>
        <v-col>
          <span v-if="value" class="text-body-2">{{ imageLabel }}</span>
          <span v-else class="grey--text text-body-2">No image selected</span>
        </v-col>
        <v-col v-if="!readonly" cols="auto">
          <v-btn small outlined @click="pickerOpen = true">
            <v-icon small left>mdi-image</v-icon> Browse
          </v-btn>
          <v-btn v-if="value" small icon @click="$emit('input', null)">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- file -->
      <v-row v-else dense align="center">
        <v-col>
          <v-text-field
            :value="value"
            placeholder="File URL or storage_key"
            dense
            outlined
            hide-details
            :readonly="readonly"
            @input="$emit('input', $event)"
          />
        </v-col>
        <v-col v-if="!readonly" cols="auto">
          <v-btn small outlined @click="pickerOpen = true">
            <v-icon small left>mdi-folder-open</v-icon> Browse
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <media-picker-dialog
      v-model="pickerOpen"
      :entity-id="entityId"
      :mime-filter="mimeFilter"
      @select="onSelect"
    />
  </div>
</template>

<script>
import { API } from '@/utils/api'
import MediaPickerDialog from '../../../media/MediaPickerDialog.vue'

const mediaUrlCache = {}

export default {
  name: 'MediaField',
  components: {
    MediaPickerDialog,
  },
  props: {
    value: {
      default: null,
    },
    mode: {
      type: String,
      required: true,
      validator: v => v === 'image' || v === 'file',
    },
    label: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pickerOpen: false,
      mediaUrls: {},
    }
  },
  computed: {
    isImage() {
      return this.mode === 'image'
    },
    mimeFilter() {
      return this.isImage ? 'image/' : ''
    },
    previewUrl() {
      return this.urlForValue(this.value)
    },
    imageLabel() {
      return typeof this.value === 'number' ? `Media #${this.value}` : String(this.value || '')
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (this.isImage) {
          this.ensureResolved(v)
        }
      },
    },
  },
  methods: {
    onSelect(file) {
      if (this.isImage) {
        mediaUrlCache[file.media_file_id] = file.url
        this.$set(this.mediaUrls, file.media_file_id, file.url)
        this.$emit('input', file.media_file_id)
      } else {
        this.$emit('input', file.url)
      }
    },
    urlForValue(v) {
      if (!v) {
        return ''
      }
      if (typeof v === 'number') {
        return this.mediaUrls[v] || ''
      }
      return String(v)
    },
    ensureResolved(v) {
      if (typeof v !== 'number' || this.mediaUrls[v]) {
        return
      }
      if (mediaUrlCache[v]) {
        this.$set(this.mediaUrls, v, mediaUrlCache[v])
        return
      }
      API.call('/cms/media-file/get.json', {
        media_file_id: v,
        entity_id: this.entityId,
      }).then(r => {
        const url = (r.data && r.data.item && r.data.item.url) || (r.data && r.data.url)
        if (!url) {
          return
        }
        mediaUrlCache[v] = url
        this.$set(this.mediaUrls, v, url)
      }).catch(() => {})
    },
  },
}
</script>
